import { DefaultView, OptionalEditView, Views } from '../../../types/BlockWithViews';
import { asFetchableBlock, useInvalidateRefreshableBlock, useRefreshBlock } from '../../../_lib/BlockFetcher';
import { BlockType } from '../../../types/api/ElementType';
import React, { ComponentProps, useCallback, useState } from 'react';
import { ChangeAccessLevelDefaultView } from './views/ChangeAccessLevelDefaultView';
import { ChangeAccessLevelEditView } from './views/ChangeAccessLevelEditView';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { BlockBar, BlockBarButton } from '../../../_lib/components/BlockBar';
import { Icons } from '../../../components/atoms/SVG';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { DeclinedAccessLevelRequestInformation } from '../common/partials/DeclinedAccessLevelRequestInformation';
import { AcceptDeclineForm } from '../../../_lib/Form2';
import { useDelayed } from '../../../hooks/useDelayed';
import { ModalDialogModifier } from '../../../components/organisms/ModalDialog/ModalDialog';
import Btn from '../../../components/molecules/Btn';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import { SendEmailForm } from './partials/SendEmailForm';
import { EmailRequestStatus } from './partials/EmailRequestStatus';

type ChangeAccessLevelProps = Views<
  DefaultView<ChangeAccessLevelDefaultView> & OptionalEditView<ChangeAccessLevelEditView>
>;

function ChangeAccessLevel({ views }: ChangeAccessLevelProps) {
  const { t } = useSonTranslation('AccessLevel');

  const [showEditModal, setShowEditModal] = useState(false);
  const [showRemovePendingRequestModal, setShowRemovePendingRequestModal] = useState(false);
  const [showEmailCheckModal, setShowEmailCheckModal] = useState(false);

  const closeEditModal = useCallback(() => {
    setShowEditModal(false);
  }, []);

  const invalidateRefreshableBlock = useInvalidateRefreshableBlock();
  const { refresh } = useRefreshBlock();

  const delayedOnRemovePendingRequestSuccess = useDelayed(() => {
    refresh();
    invalidateRefreshableBlock(BlockType.currentAccessLevel);
    setShowRemovePendingRequestModal(false);
  });

  const pendingAccessLevelLabel = t('labels.pendingAccessLevelLabel');
  let blockBarHeading: string | undefined = undefined;
  let accessLevelTitleLabel = '';
  if (views.default.embedded.pendingRequest?.requestedLevel) {
    accessLevelTitleLabel = t(
      `AccessLevel:form.accessLevels.${views.default.embedded.pendingRequest.requestedLevel}.title`
    );
    blockBarHeading = `${pendingAccessLevelLabel}: ${accessLevelTitleLabel}`;
  }

  const declinedRequest = views.default.embedded.declinedRequest;
  const removeDeclinedRequest = views.default.embedded.removeDeclinedRequest;
  const removePendingRequest = views.default.embedded.removePendingRequest;
  const sendEmailForm = views.default.embedded.sendEmailForm;
  const emailStatus = views.default.embedded.emailStatus;

  const mandateContactPersonEmail = views.default.embedded.pendingRequest?.mandateContactPersonEmail;

  return (
    <>
      {blockBarHeading && (
        <>
          <BlockBar heading={blockBarHeading} hierarchy={3}>
            <div className="access-level__pending-request-button-container">
              {sendEmailForm ? (
                <div className="access-level__email-request-button">
                  <Btn
                    svgSpriteId={Icons.MAIL_ENVELOPE}
                    type="button"
                    text={t('buttons.checkEmail')}
                    hiddenText
                    titleText={t('buttons.checkEmail')}
                    tooltipPlacement={TooltipPlacement.TOP}
                    onClick={() => setShowEmailCheckModal(true)}
                  />
                </div>
              ) : null}
              {views.edit ? (
                <BlockBarButton
                  onClick={() => {
                    setShowEditModal(true);
                  }}
                  text={t('editRequest')}
                  svgSpriteId={Icons.EDIT_PENCIL}
                />
              ) : null}
              {removePendingRequest ? (
                <BlockBarButton
                  text={t('removePendingRequest.headline', { name: accessLevelTitleLabel })}
                  onClick={() => setShowRemovePendingRequestModal(!showRemovePendingRequestModal)}
                  svgSpriteId={Icons.DELETE_TRASHCAN}
                />
              ) : null}
            </div>
          </BlockBar>
          {emailStatus ? <EmailRequestStatus emailStatus={emailStatus} /> : null}
          <ChangeAccessLevelDefaultView {...views.default} />
        </>
      )}
      {declinedRequest && (
        <DeclinedAccessLevelRequestInformation
          declinedRequest={declinedRequest}
          removeDeclinedRequestForm={removeDeclinedRequest}
        />
      )}
      {views.edit && showEditModal && (
        <ModalDialog
          closeModal={closeEditModal}
          headline={t('form.headline.edit')}
          modifier={ModalDialogModifier.WITH_NAVIGATION}
        >
          <ChangeAccessLevelEditView {...views.edit} />
        </ModalDialog>
      )}
      {showRemovePendingRequestModal && removePendingRequest ? (
        <ModalDialog
          closeModal={() => setShowRemovePendingRequestModal(false)}
          headline={t('removePendingRequest.headline', { name: accessLevelTitleLabel })}
        >
          <AcceptDeclineForm
            headline={t('removePendingRequest.description')}
            form={removePendingRequest}
            onSuccess={delayedOnRemovePendingRequestSuccess}
            messageSuccess={t('removePendingRequest.status.success')}
            messageSending={t('removePendingRequest.status.sending')}
            messageFail={t('removePendingRequest.status.fail')}
          />
        </ModalDialog>
      ) : null}
      {showEmailCheckModal && sendEmailForm && mandateContactPersonEmail ? (
        <ModalDialog
          closeModal={() => setShowEmailCheckModal(false)}
          headline={t('checkEmail.headline', { name: accessLevelTitleLabel })}
        >
          <SendEmailForm form={sendEmailForm} mandateContactPersonEmail={mandateContactPersonEmail} />
        </ModalDialog>
      ) : null}
    </>
  );
}

export const ChangeAccessLevelBlock = asFetchableBlock(
  ChangeAccessLevel,
  BlockType.changeAccessLevel,
  BlockType.changeAccessLevel // we need to register this block for useInvalidateRefreshableBlock
);
export type ChangeAccessLevelBlock = ComponentProps<typeof ChangeAccessLevelBlock>;
